import { formatApiErrorMessage, getApiErrorDependencies, getApiErrorMessage } from '@/api/apiError'
import { getApiAccessFailureMessage } from '@/components/access-control/routeAccessMessages'
import {
	buildErrorMessageWithDependencies,
	hasDependencies,
	resolveDependencyListsAsync,
} from '@/components/common/DependencyWarning'
import type { ResourceId } from '@/config/access-control'
import type { Permissions } from '@/rbac/grants'
import type { ApiError } from '@/stores/auth'
import { toast } from 'sonner'

export interface AccessErrorContext {
	resource?: ResourceId
	viewPermission?: keyof Permissions
	complexPermissionKey?: keyof Permissions
	permissions?: Permissions
}

export interface ErrorOptions {
	showToast?: boolean
	toastMessage?: string
	logError?: boolean
	onError?: (error: ApiError) => void
}

export const getUserFriendlyMessage = (
	error: ApiError | Error | unknown,
	accessContext?: AccessErrorContext,
): string => {
	if (typeof error === 'object' && error !== null) {
		if ('status' in error) {
			const apiError = error as ApiError
			switch (apiError.status) {
				case 401:
					return 'Your session has expired. Please log in again.'
				case 403: {
					if (accessContext?.resource) {
						const contextual = getApiAccessFailureMessage({
							status: 403,
							permissions: accessContext.permissions,
							resource: accessContext.resource,
							viewPermission: accessContext.viewPermission,
							complexPermissionKey: accessContext.complexPermissionKey,
						})
						if (contextual) {
							return contextual
						}
					}
					return 'You do not have permission to perform this action.'
				}
				case 404:
					return 'The requested resource was not found.'
				case 500:
					return 'An internal server error occurred. Please try again later.'
				case 503:
					return 'The service is temporarily unavailable. Please try again later.'
				default:
					return apiError.message || 'An unexpected error occurred.'
			}
		}
		if ('message' in error) {
			return (error as Error).message
		}
	}
	return 'An unexpected error occurred.'
}

/** A failed upload's message. The size limit and access denial are named; anything else gets the API's reason. */
export function uploadErrorMessage(error: unknown, fallback: string): string {
	switch (getApiErrorStatus(error)) {
		case 413:
			return 'Upload failed due to size limit'
		case 401:
			return 'Upload failed: access denied'
		default:
			return formatApiErrorMessage(fallback, error)
	}
}

export function getApiErrorStatus(error: unknown): number | undefined {
	if (typeof error === 'object' && error !== null && 'status' in error) {
		const status = (error as ApiError).status
		return typeof status === 'number' ? status : undefined
	}
	return undefined
}

/**
 * True when a request was cancelled client-side (AbortController / axios `CanceledError`) rather
 * than failing — the caller was superseded, so it must leave the state to the newer request.
 *
 * `apiCall` funnels every rejection through `mapApiError`, which returns a plain `ApiError` object.
 * That strips the `CanceledError` prototype, so an `instanceof Error` check alone misses every
 * cancellation raised by the API layer; match the mapped shape (`code`/`message`) too.
 */
export const isAbortError = (error: unknown): boolean => {
	if (error instanceof Error && (error.name === 'CanceledError' || error.name === 'AbortError')) {
		return true
	}
	if (typeof error === 'object' && error !== null) {
		const { code, message } = error as { code?: unknown; message?: unknown }
		if (code === 'ERR_CANCELED') {
			return true
		}
		return typeof message === 'string' && message.trim().toLowerCase() === 'canceled'
	}
	return false
}

export const normalizeError = (error: unknown): ApiError => {
	if (typeof error === 'object' && error !== null && 'status' in error) {
		return error as ApiError
	}
	if (error instanceof Error) {
		return {
			message: error.message,
			status: 500,
		}
	}
	return {
		message: 'An unexpected error occurred',
		status: 500,
	}
}

export const handleApiError = (
	error: unknown,
	options: ErrorOptions = {},
	accessContext?: AccessErrorContext,
): ApiError => {
	const { showToast = true, toastMessage, logError = process.env.NODE_ENV === 'development', onError } = options

	const apiError = normalizeError(error)
	const userMessage = toastMessage || getUserFriendlyMessage(apiError, accessContext)

	if (logError) {
		console.error('API Error:', {
			message: apiError.message,
			status: apiError.status,
			code: apiError.code,
			error,
		})
	}

	if (showToast && apiError.status !== 401) {
		toast.error(userMessage)
	}

	if (onError) {
		onError(apiError)
	}

	return apiError
}

/**
 * Recursively unwrap a string that may be a JSON body of shape `{"error_message":"..."}`.
 * Handles nested wrapping (JSON inside JSON inside JSON...) up to a small depth cap.
 */
const unwrapErrorMessageString = (raw: string, maxDepth = 5): string => {
	let current = raw
	for (let i = 0; i < maxDepth; i++) {
		const trimmed = current.trim()
		if (!trimmed.startsWith('{')) {
			return current
		}
		try {
			const parsed = JSON.parse(trimmed)
			const next = (parsed as { error_message?: unknown })?.error_message
			if (typeof next !== 'string' || next === current) {
				return current
			}
			current = next
		} catch {
			return current
		}
	}
	return current
}

/**
 * Extract a clean, user-facing error message from any thrown value.
 * Handles ApiError, Error, string, and unwraps JSON bodies of shape `{"error_message":"..."}`.
 * Caller MUST supply a domain-specific fallback (e.g. 'Failed to load panel data').
 */
export const extractErrorMessage = (error: unknown, fallback: string): string => {
	// 5xx text is upstream detail (internal service URLs, transport errors), not something a user can act on.
	if ((getApiErrorStatus(error) ?? 0) >= 500) {
		return fallback
	}
	const extracted = getApiErrorMessage(error)
	if (!extracted) {
		return fallback
	}
	return unwrapErrorMessageString(extracted)
}

/**
 * Async version of `extractErrorMessage` that resolves backend dependency IDs to display names
 * (dashboards, session rules, exporters, DBC parsers) when `dependencies` is present on the error response.
 */
export const extractErrorMessageAsync = async (error: unknown, fallback: string): Promise<string> => {
	const baseMessage = extractErrorMessage(error, fallback)
	const dependencies = getApiErrorDependencies(error)
	if (dependencies && hasDependencies(dependencies)) {
		try {
			const resolved = await resolveDependencyListsAsync(dependencies)
			return buildErrorMessageWithDependencies(baseMessage, resolved)
		} catch (err) {
			console.error('Failed to resolve dependency names for error:', err)
		}
	}
	return baseMessage
}

/** Status-carrying error for store `error` state — preserves HTTP status so 403s stay distinguishable downstream. */
export interface PageError {
	message: string
	status?: number
}

/** Normalize any caught value into a `PageError`, preserving HTTP status (message falls back to `fallback`). */
export const toPageError = (error: unknown, fallback: string): PageError => ({
	message: extractErrorMessage(error, fallback),
	status: getApiErrorStatus(error),
})

/** Async version of `toPageError` that resolves backend dependency IDs to display names. */
export const toPageErrorAsync = async (error: unknown, fallback: string): Promise<PageError> => ({
	message: await extractErrorMessageAsync(error, fallback),
	status: getApiErrorStatus(error),
})

export const isNetworkError = (error: unknown): boolean => {
	if (typeof error === 'object' && error !== null) {
		if ('status' in error) {
			const apiError = error as ApiError
			return apiError.status === 0 || (!apiError.status && 'message' in error)
		}
	}
	return false
}

export const isRetryableError = (error: unknown): boolean => {
	const apiError = normalizeError(error)
	return isNetworkError(error) || (apiError.status >= 500 && apiError.status < 600)
}
